import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import styled from 'styled-components';
import React, { useCallback, useEffect, useState } from 'react';
import { IinventoryItem } from '@typings/store/storeItem.interface';
import {
  PET_SUB_FUNCTION_TYPE,
  ITEM_FUNCTION_TPYE,
} from '@typings/store/storeItemFunctionType';
import {
  LevelSystem,
  PetDefault,
  PetInteractionLog,
  UserPet,
} from '@typings/pets/pet.interface';
import useLevelCheck from '@pages/Pets/hooks/useLevelCheck';
import ItemDropdown from '@pages/Pets/components/ItemDropdown/ItemDropdown';
import StatusBar from '@pages/Pets/components/StatusBar/StatusBar';
import { useCollection, fuego, useDocument } from '@nandorojo/swr-firestore';
import {
  CollectionPath,
  SettingCollectionPath,
} from '@typings/collections.enum';
import { deleteItemsFromInventory } from '@store/firebase/store/parchase-item';
import { mainPhotocardDataState } from '@atom/setting/photocardAutoSetState';

const ItemControlsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const StatusSec = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

type Props = {
  levelSystemData?: LevelSystem | null;
  activePet: any;
  setUserPets: (any) => void;
};

function ItemControls({ levelSystemData, activePet, setUserPets }: Props) {
  const currentUid = useRecoilValue(currentUserIDState);
  const representativeCharacter = useRecoilValue(mainPhotocardDataState);
  const activePetId = activePet?.id;
  // 뷰 즉시 반영을 위한 로컬 표시 값 (Firestore 반영 전 낙관적 업데이트)
  const [viewHunger, setViewHunger] = useState<number>(activePet?.hunger || 0);
  const [viewExperience, setViewExperience] = useState<number>(
    activePet?.experience || 0,
  );
  const photocardPath =
    representativeCharacter?.path || CollectionPath.PHOTOCARD;

  // 현재 활성 펫 문서 path 및 set 함수 (저장용)
  const petDocPath =
    currentUid && activePetId
      ? `${CollectionPath.USERS}/${currentUid}/pets/${activePetId}`
      : '';
  const { set: setActivePetDoc } = useDocument<UserPet>(petDocPath, {
    listen: false,
  });

  // 기본 설정 최대치 포만감 불러오기
  const { data: petDefaultSetting } = useDocument<PetDefault>(
    `${SettingCollectionPath.PET}`,
    { listen: true },
  );
  const maxHunger = petDefaultSetting?.maxHunger || 100;

  // activePet 변경 시 동기화
  useEffect(() => {
    setViewHunger(activePet?.hunger || 0);
    setViewExperience(activePet?.experience || 0);
  }, [activePet?.id, activePet?.hunger, activePet?.experience]);

  // 인벤토리 아이템 가져오기
  const { data: petItems, error } = useCollection<IinventoryItem>(
    `${photocardPath}/${representativeCharacter?.id}/inventory`,
    {
      listen: true,
      orderBy: ['createdAt', 'desc'],
    },
  );

  const { getMaxExpForLevel } = useLevelCheck(levelSystemData);

  // 🍽️ 아이템 사용
  const handleUseItem = useCallback(
    async (itemId: string, quantity: number): Promise<void> => {
      if (!activePet || !representativeCharacter?.id || !petItems) return;

      try {
        const item = petItems.find((i) => i.id === itemId);
        if (!item || !item.petFunction) {
          throw new Error('아이템을 찾을 수 없습니다.');
        }

        const availableQuantity = item.multiple ? item.multiple.length : 0;
        if (availableQuantity < quantity) {
          throw new Error('사용할 수 있는 아이템이 부족합니다.');
        }

        // 🔥 Firebase에서 아이템 차감
        await deleteItemsFromInventory({
          data: {
            ...item,
            functionType: item.functionType || ITEM_FUNCTION_TPYE.PET,
          },
          amount: quantity,
          collectionPath: `${CollectionPath.PHOTOCARD}/${representativeCharacter.id}/inventory`,
        });

        // 🐾 펫 상태 업데이트
        const updatedPet = { ...activePet };
        const effectValue = Number(item.petFunction.value) || 0;

        if (
          item.petFunction.subFunctionType === PET_SUB_FUNCTION_TYPE.FILL_HUNGER
        ) {
          // 현재 배고픔 값을 안전하게 처리
          const currentHunger = Number(updatedPet.hunger) || 0;
          updatedPet.hunger = Math.min(
            maxHunger,
            currentHunger + effectValue * quantity,
          );
          updatedPet.lastFeedTime = new Date();
          // 즉시 반영
          setViewHunger(updatedPet.hunger);
          // Persist
          if (setActivePetDoc) {
            await setActivePetDoc(
              {
                hunger: updatedPet.hunger,
                lastFeedTime: updatedPet.lastFeedTime,
              } as any,
              { merge: true },
            );
          }
        } else if (
          item.petFunction.subFunctionType === PET_SUB_FUNCTION_TYPE.FILL_EXP
        ) {
          // 현재 경험치 값을 안전하게 처리 (undefined나 NaN 방지)
          const currentExp = Number(updatedPet.experience) || 0;
          const addedExp = effectValue * quantity;
          updatedPet.experience = currentExp + addedExp;
          updatedPet.lastPlayTime = new Date();
          // 즉시 반영
          setViewExperience(updatedPet.experience);
          // Persist: 경험치/최근 놀이 시간만 반영 (레벨업은 useAutoLevelUp에서 처리)
          if (setActivePetDoc) {
            await setActivePetDoc(
              {
                experience: updatedPet.experience,
                lastPlayTime: updatedPet.lastPlayTime,
              } as any,
              { merge: true },
            );
          }
          console.log(
            `✅ 경험치 업데이트: ${currentExp} + ${addedExp} = ${updatedPet.experience}`,
          );
        }

        // 로컬 상태 업데이트
        setUserPets((prev) =>
          prev.map((pet) => (pet.id === activePet.id ? updatedPet : pet)),
        );

        // 안전한 라벨 생성
        const getItemLabel = (it: any) =>
          it?.title || it?.customTitle || it?.name || '아이템';

        const tags: string[] = [];
        if (effectValue > 0) tags.push(`경험치:+${effectValue * quantity}`);
        if (effectValue < 0)
          tags.push(`배고픔:-${Math.abs(effectValue * quantity)}`);
        const tagStr = tags.length ? ` [${tags.join(' | ')}]` : '';
        // 📝 상호작용 로그 추가
        const actionType =
          item.petFunction.subFunctionType === PET_SUB_FUNCTION_TYPE.FILL_HUNGER
            ? '먹이주기'
            : '놀아주기';
        const logMessage = `${getItemLabel(
          item,
        )} ${quantity}개를 사용했습니다. (${actionType}) ${tagStr}`;

        const newLog: PetInteractionLog = {
          id: `log_${Date.now()}`,
          userId: currentUid || 'unknown',
          petId: activePet.id,
          type: 'item_use',
          message: logMessage,
          createdAt: new Date(),
        };
        // 아이템 사용 시 로그 출력
        // 저장: users/{uid}/pets/{petId}/logs
        try {
          const ownerId = activePet.userId || currentUid;
          if (ownerId) {
            await fuego.db
              .collection(
                `${CollectionPath.USERS}/${ownerId}/pets/${activePet.id}/logs`,
              )
              .add(newLog as any);
          }
        } catch (e) {
          console.warn('아이템 사용 로그 저장 실패', e);
        }

        console.log(`✅ 아이템 사용 완료: ${getItemLabel(item)} x${quantity}`);
      } catch (error) {
        console.error('❌ 아이템 사용 실패:', error);
        throw error; // ItemDropdown에서 에러 처리
      }
    },
    [
      activePet,
      representativeCharacter,
      petItems,
      currentUid,
      setUserPets,
      setActivePetDoc,
      maxHunger,
    ],
  );

  if (!representativeCharacter) {
    return (
      <ItemControlsWrap>
        <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
          대표 캐릭터를 찾을 수 없습니다.
        </div>
      </ItemControlsWrap>
    );
  }

  return (
    <ItemControlsWrap>
      {/* 밥주기 */}
      <StatusSec>
        <StatusBar
          type="hunger"
          current={viewHunger}
          max={maxHunger}
          label="배고픔"
        />
        <ItemDropdown
          type="food"
          items={petItems || []}
          onUseItem={handleUseItem}
          petHunger={activePet.hunger}
          petMaxHunger={maxHunger}
        />
      </StatusSec>

      {/* 놀아주기 */}
      <StatusSec>
        <StatusBar
          type="experience"
          current={viewExperience}
          max={getMaxExpForLevel(activePet.level)} // 레벨 시스템의 필요 경험치 반영
          label="경험치"
          showMax={levelSystemData?.isActive}
        />
        <ItemDropdown
          type="toy"
          items={petItems || []}
          onUseItem={handleUseItem}
        />
      </StatusSec>
    </ItemControlsWrap>
  );
}

export default ItemControls;
